
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Device } from '@twilio/voice-sdk';
// import { Phone, Mic, MicOff, PhoneOff } from 'lucide-react'; // Assuming we have these or similar icons

const CallPage = () => {
    const { token } = useParams();
    const [status, setStatus] = useState('Initializing...');
    const [device, setDevice] = useState(null);
    const [call, setCall] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [error, setError] = useState(null);
    const [callDetails, setCallDetails] = useState(null);
    const [mode, setMode] = useState('browser'); // 'browser' | 'agent'

    // Audio visualization ref (for agent mode)
    const audioVisualizerRef = useRef(null);

    useEffect(() => {
        if (token) {
            fetchCallDetails();
            initializeDevice();
        }

        return () => {
            if (device) {
                device.destroy();
            }
        };
    }, [token]);

    const fetchCallDetails = async () => {
        try {
            // In a real app we would have an endpoint to get call details by token
            // For now, we'll just use the token to identify the call
            // const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/calls/by-token/${token}`);
            // const data = await res.json();
            // setCallDetails(data);
            // setMode(data.mode);
            setStatus('Ready to Connect');
        } catch (err) {
            console.error('Failed to fetch call details', err);
            setError('Invalid call link');
        }
    };

    const initializeDevice = async () => {
        try {
            setStatus('Setting up audio...');

            // Get capability token from backend
            // Note: In production we need a way to auth this request or use a public token endpoint
            // For now assuming we can fetch it (maybe with a temp session or just valid API key if public)
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/calls/capability-token`);
            const data = await res.json();

            if (!data.token) throw new Error('Failed to get capability token');

            const newDevice = new Device(data.token, {
                codecPreferences: ['opus', 'pcmu'],
                fakeLocalDTMF: true,
                enableRingingState: true
            });

            newDevice.on('ready', () => {
                setStatus('Ready');
                console.log('Twilio Device Ready');
            });

            newDevice.on('error', (error) => {
                console.error('Twilio Device Error:', error);
                setError(`Audio Error: ${error.message}`);
            });

            newDevice.on('connect', (conn) => {
                setStatus('Call Connected');
                setCall(conn);
            });

            newDevice.on('disconnect', () => {
                setStatus('Call Ended');
                setCall(null);
            });

            await newDevice.register();
            setDevice(newDevice);

        } catch (err) {
            console.error('Device setup details:', err);
            setError('Could not access microphone or setup audio');
        }
    };

    const handleConnect = async () => {
        if (!device) return;

        try {
            setStatus('Connecting...');
            // Use the token or call ID as param to the TwiML app to know which call to join
            const params = { call_token: token };
            await device.connect({ params });
        } catch (err) {
            setError('Connection failed');
        }
    };

    const handleHangup = () => {
        if (device) {
            device.disconnectAll();
        }
    };

    const toggleMute = () => {
        if (call) {
            call.mute(!isMuted);
            setIsMuted(!isMuted);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">

                {/* Header */}
                <div className="p-6 text-center border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
                    <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                        GetCall
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">Secure Call Gateway</p>
                </div>

                {/* Main Content */}
                <div className="p-8 flex flex-col items-center">

                    {/* Avatar / Visualizer */}
                    <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center mb-6 relative overflow-hidden shadow-inner ring-4 ring-gray-600/30">
                        {status === 'Call Connected' ? (
                            <div className="absolute inset-0 bg-green-500/20 animate-pulse"></div>
                        ) : null}
                        <span className="text-4xl">📞</span>
                    </div>

                    {/* Status Text */}
                    <h3 className="text-xl font-semibold mb-2">{error || status}</h3>
                    <p className="text-gray-400 text-sm mb-8 text-center">
                        {callDetails ? `Calling ${callDetails.to_number}` : 'Waiting to connect...'}
                    </p>

                    {/* Controls */}
                    {error ? (
                        <button onClick={() => window.location.reload()} className="px-6 py-2 bg-gray-700 rounded-full hover:bg-gray-600 transition">
                            Retry
                        </button>
                    ) : (
                        <div className="flex gap-6">
                            {!call ? (
                                <button
                                    onClick={handleConnect}
                                    disabled={status !== 'Ready'}
                                    className={`w-16 h-16 rounded-full flex items-center justify-center transition shadow-lg transform hover:scale-105 ${status === 'Ready' ? 'bg-green-500 hover:bg-green-400 text-white' : 'bg-gray-600 cursor-not-allowed opacity-50'
                                        }`}
                                >
                                    📞
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={toggleMute}
                                        className={`w-14 h-14 rounded-full flex items-center justify-center transition shadow border ${isMuted ? 'bg-white text-gray-900 border-white' : 'bg-transparent border-gray-500 text-gray-300 hover:bg-gray-700'
                                            }`}
                                    >
                                        {isMuted ? '🔇' : '🎤'}
                                    </button>

                                    <button
                                        onClick={handleHangup}
                                        className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition shadow-lg transform hover:scale-105"
                                    >
                                        End
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                </div>

                {/* Footer info */}
                <div className="p-4 bg-gray-900/50 text-center text-xs text-gray-500 border-t border-gray-700">
                    Encrypted • WebRTC • Low Latency
                </div>

            </div>
        </div>
    );
};

export default CallPage;
